import { supabase } from './supabase'
import { fetchAllPaged, fetchByIdsChunked } from './pagination'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

export interface SalesPerRepFilters {
  from: string   // yyyy-MM-dd inclusive
  to: string     // yyyy-MM-dd inclusive
  search: string // rep name
}

export interface SalesRepRow {
  repId: string | null             // null = Unassigned bucket
  repName: string
  customersAssigned: number | null // live count; null for Unassigned
  salesCount: number
  gross: number
  paid: number
  outstanding: number
  avgSize: number
  topCustomerName: string | null
}

export interface SalesPerRepResult {
  rows: SalesRepRow[]
  totalSales: number
  totalReps: number
}

interface SaleRow {
  total: number | null
  amount_paid: number | null
  customer_id: string | null
  representative_id: string | null
  representative_name_snapshot: string | null
  meta: { opening_balance_bf?: boolean } | null
}

const UNASSIGNED = '__unassigned__'

async function fetchPeriodSales(tid: string, filters: SalesPerRepFilters): Promise<SaleRow[]> {
  return await fetchAllPaged<SaleRow>((pageFrom, pageTo) =>
    db('sales_orders')
      // meta: opening-balance brought-forward invoices are a carried ledger balance,
      // not a sale — filtered out in the aggregation below.
      .select('id, total, amount_paid, customer_id, representative_id, representative_name_snapshot, meta')
      .eq('tenant_id', tid)
      .not('status', 'in', '("cancelled","refunded")')
      .gte('created_at', filters.from + 'T00:00:00')
      .lte('created_at', filters.to + 'T23:59:59')
      .order('id', { ascending: true })
      .range(pageFrom, pageTo))
}

async function fetchCustomerCountsByRep(tid: string): Promise<Map<string, number>> {
  const rows = await fetchAllPaged<{ representative_id: string | null }>((from, to) =>
    db('customers')
      .select('id, representative_id')
      .eq('tenant_id', tid)
      .not('representative_id', 'is', null)
      .order('id', { ascending: true })
      .range(from, to))
  const m = new Map<string, number>()
  for (const r of rows) {
    if (!r.representative_id) continue
    m.set(r.representative_id, (m.get(r.representative_id) ?? 0) + 1)
  }
  return m
}

export async function fetchSalesPerRep(tid: string, filters: SalesPerRepFilters): Promise<SalesPerRepResult> {
  const [sales, custCounts] = await Promise.all([
    fetchPeriodSales(tid, filters),
    fetchCustomerCountsByRep(tid),
  ])

  interface Agg {
    repId: string | null
    repName: string
    salesCount: number
    gross: number
    paid: number
    byCustomer: Map<string, number>
  }
  const groups = new Map<string, Agg>()

  for (const s of sales) {
    if (s.meta?.opening_balance_bf === true) continue   // carried ledger balance, not a sale
    const key = s.representative_id ?? UNASSIGNED
    let g = groups.get(key)
    if (!g) {
      g = {
        repId: s.representative_id ?? null,
        repName: s.representative_id ? (s.representative_name_snapshot ?? 'Unknown rep') : 'Unassigned',
        salesCount: 0, gross: 0, paid: 0, byCustomer: new Map(),
      }
      groups.set(key, g)
    }
    if (s.representative_id && s.representative_name_snapshot) g.repName = s.representative_name_snapshot
    const total = Number(s.total ?? 0)
    g.salesCount += 1
    g.gross += total
    g.paid += Number(s.amount_paid ?? 0)
    if (s.customer_id) g.byCustomer.set(s.customer_id, (g.byCustomer.get(s.customer_id) ?? 0) + total)
  }

  const topIdByGroup = new Map<string, string | null>()
  const allTopIds = new Set<string>()
  for (const [key, g] of groups) {
    let topId: string | null = null
    let topVal = -Infinity
    for (const [cid, val] of g.byCustomer) {
      if (val > topVal) { topVal = val; topId = cid }   // first-max wins on ties
    }
    topIdByGroup.set(key, topId)
    if (topId) allTopIds.add(topId)
  }
  const nameRows = allTopIds.size
    ? await fetchByIdsChunked<{ id: string; name: string | null; business_name: string | null }>(
        [...allTopIds], batch => db('customers').select('id, name, business_name').in('id', batch))
    : []
  const nameMap = new Map(nameRows.map(r => [r.id, r.business_name || r.name || '—']))

  const rows: SalesRepRow[] = [...groups.entries()].map(([key, g]) => {
    const topId = topIdByGroup.get(key) ?? null
    return {
      repId: g.repId,
      repName: g.repName,
      customersAssigned: g.repId ? (custCounts.get(g.repId) ?? 0) : null,
      salesCount: g.salesCount,
      gross: g.gross,
      paid: g.paid,
      outstanding: g.gross - g.paid,
      avgSize: g.salesCount ? g.gross / g.salesCount : 0,
      topCustomerName: topId ? (nameMap.get(topId) ?? '—') : null,
    }
  })

  rows.sort((a, b) => {
    if (a.repId === null) return 1       // Unassigned always last
    if (b.repId === null) return -1
    return b.gross - a.gross
  })

  const term = filters.search.trim().toLowerCase()
  const filtered = term ? rows.filter(r => r.repId !== null && r.repName.toLowerCase().includes(term)) : rows

  return {
    rows: filtered,
    totalSales: sales.length,
    totalReps: [...groups.values()].filter(g => g.repId !== null).length,
  }
}

export interface SalesRepExportRow extends Record<string, unknown> {
  representative: string
  customers_assigned: number | string
  sales_count: number
  gross_sales: number
  paid: number
  outstanding: number
  avg_sale_size: number
  top_customer: string
}

export function toSalesRepExportRow(r: SalesRepRow): SalesRepExportRow {
  return {
    representative: r.repName,
    customers_assigned: r.customersAssigned ?? '',
    sales_count: r.salesCount,
    gross_sales: r.gross,
    paid: r.paid,
    outstanding: r.outstanding,
    avg_sale_size: r.avgSize,
    top_customer: r.topCustomerName ?? '',
  }
}

export async function fetchSalesPerRepExport(tid: string, filters: SalesPerRepFilters): Promise<SalesRepExportRow[]> {
  const { rows } = await fetchSalesPerRep(tid, filters)
  return rows.map(toSalesRepExportRow)
}
